import React from "react";
import { Box, Typography, LinearProgress, Stack } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import PieChartOutlineRoundedIcon from "@mui/icons-material/PieChartOutlineRounded";
import { IBuyBack } from "../shared/types";
import { SurfaceCard } from "../components/ui/SurfaceCard";
import { SectionHeader } from "../components/ui/SectionHeader";
import { StatusPill, StatusTone } from "../components/ui/StatusPill";
import { ChainAvatar, chainName, chainOf } from "../components/ui/ChainAvatar";
import { tokens } from "../theme";
import { formatDateTime, formatPairPrice, formatTokenAmount } from "../utils/formatters";
import { botLongLabel } from "../utils/botNames";
import { useResponsive } from "../hooks/useResponsive";

/** One precision rule for every amount on the page (token or quote): see formatTokenAmount. */
const formatNumber = (num: number, _decimals?: number) => (Number.isFinite(num) ? formatTokenAmount(num) : "—");

/**
 * Open tasks of one pair (backend `PendingBuybackSummary`). Buy figures are quote amounts and sell
 * figures base amounts: shown apart, never added. Older backends send buys only (no buy/sell fields).
 */
interface PendingPairSummary {
  baseAsset: string;
  quoteAsset: string;
  /** Open tasks of both kinds. */
  count: number;
  /** Buys: quote still to spend. */
  remainingBudget: number;
  accountQuoteBalance?: number;
  buyCount?: number;
  sellCount?: number;
  /** Sells: base still to sell. */
  remainingSellBase?: number;
}

const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? "" : "s"}`;

/** Badge of a pair: "2 buys · 1 sell"; "3 tasks" from backends without the split. */
const taskMix = (s: PendingPairSummary) => {
  if (s.buyCount == null && s.sellCount == null) return plural(s.count, "task");
  const parts = [s.buyCount ? plural(s.buyCount, "buy") : "", s.sellCount ? plural(s.sellCount, "sell") : ""].filter(Boolean);
  return parts.length ? parts.join(" · ") : plural(s.count, "task");
};

const hasSells = (s: PendingPairSummary) => (s.sellCount ?? 0) > 0 || (s.remainingSellBase ?? 0) > 0;
/** Buys are shown unless the pair only has sells (old backends: always). */
const hasBuys = (s: PendingPairSummary) => s.buyCount == null || s.buyCount > 0 || s.remainingBudget > 0 || !hasSells(s);

const formatDate = (timestamp?: number) => formatDateTime(timestamp);

/** Average fill price with the pair formatter: "$2,689.16", "$0.6118" (sub-dollar prices keep 4 decimals). */
const formatAvgPrice = (item: IBuyBack) =>
  Number.isFinite(item.avgBuyPrice) && item.avgBuyPrice > 0
    ? formatPairPrice(item.avgBuyPrice, item.baseAsset, item.quoteAsset, Math.abs(item.avgBuyPrice) < 1 ? 4 : 2)
    : "—";

/** In progress = gold, failed = red, done / paused / not started = neutral (not started gets a hollow dot). */
const statusTone = (status: string): StatusTone => {
  switch (status) {
    case "FAILED":
      return "error";
    case "ACTIVE":
      return "brand";
    default:
      return "neutral";
  }
};

/** Bot as "USDC / WETH · Bot 1" after its chain's logo (the same pair runs on several chains); raw id in the title. */
const BotLabel: React.FC<{ botId: string; size: "md" | "sm" }> = ({ botId, size }) => (
  <Stack direction="row" alignItems="center" spacing={0.75} title={botId} sx={{ minWidth: 0 }}>
    <ChainAvatar chain={chainOf(botId)} size={size === "md" ? 16 : 14} />
    <Typography
      noWrap
      sx={size === "md" ? { fontSize: "0.9375rem", fontWeight: 700, minWidth: 0 } : { fontSize: "0.875rem", fontWeight: 650, minWidth: 0 }}
    >
      {botLongLabel(botId)}
    </Typography>
  </Stack>
);

const titleCase = (s: string) => s.charAt(0) + s.slice(1).toLowerCase();

/** Unit after an amount: small muted suffix. */
const Unit: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component="span" sx={{ ml: 0.5, fontSize: "0.75rem", fontWeight: 600, color: "text.secondary" }}>
    {children}
  </Box>
);

/** Not applicable / nothing yet: "—" in the disabled text colour. */
const Dash: React.FC = () => (
  <Box component="span" sx={{ color: "text.disabled" }}>
    —
  </Box>
);

/** Amount that reads "—" when zero (nothing bought / pending yet). */
const amount = (n: number | null | undefined) => (n && Number.isFinite(n) ? formatTokenAmount(n) : null);
/** Balance: a real zero stays "0"; only a missing balance reads "—". */
const balance = (n: number | null | undefined) => (n != null && Number.isFinite(n) ? formatTokenAmount(n) : null);

/** Label over a figure; a missing figure ("—") drops its unit. */
const Stat: React.FC<{ label: string; value: string | null; unit?: string }> = ({ label, value, unit }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" component="div" noWrap>
      {label}
    </Typography>
    <Typography sx={{ fontSize: "0.9375rem", fontWeight: 650 }} noWrap>
      {value == null || value === "—" ? (
        <Dash />
      ) : (
        <>
          {value}
          {unit && <Unit>{unit}</Unit>}
        </>
      )}
    </Typography>
  </Box>
);

/** Amount followed by its unit, or a disabled "—". */
const Figure: React.FC<{ value: string | null; unit: string }> = ({ value, unit }) =>
  value == null ? (
    <Dash />
  ) : (
    <>
      {value}
      <Unit>{unit}</Unit>
    </>
  );

const EmptyState: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      p: { xs: 3, md: 4 },
      textAlign: "center",
      borderRadius: "16px",
      border: `1px dashed ${tokens.borderStrong}`,
    }}
  >
    <Typography variant="body2" color="text.secondary">
      {children}
    </Typography>
  </Box>
);

interface BuybacksViewProps {
  buybacksData?: { pending: IBuyBack[]; completed: IBuyBack[] };
  pendingSummary?: Record<string, PendingPairSummary>;
}

const BuybacksView: React.FC<BuybacksViewProps> = ({ buybacksData, pendingSummary }) => {
  const theme = useTheme();
  const { isPhone } = useResponsive();

  // Filter to only show DCA tasks (not RIF_BUY or RIF_SELL)
  const pending: IBuyBack[] = (buybacksData?.pending ?? []).filter(
    (b: IBuyBack) => b.taskType === "DCA" || (!b.taskType && b.baseAsset !== "RIF")
  );
  const completed: IBuyBack[] = (buybacksData?.completed ?? []).filter(
    (b: IBuyBack) => b.taskType === "DCA" || (!b.taskType && b.baseAsset !== "RIF")
  );

  // Filter pendingSummary to exclude RIF (DIPSTER/DUMPER)
  const summaryEntries = Object.entries(pendingSummary ?? {}).filter(([key]) => !key.startsWith("RIF/"));

  // Pairs funded from the same subaccount report the same balance: state it once, not per card.
  const balanceKeys = new Set(summaryEntries.map(([, st]) => `${st.quoteAsset}:${st.accountQuoteBalance ?? 0}`));
  const shared = summaryEntries.length > 1 && balanceKeys.size === 1 ? summaryEntries[0][1] : null;
  // Buy budgets only (quote): sells are base amounts and never join this sum.
  const sharedPending = shared ? summaryEntries.reduce((acc, [, st]) => acc + (st.remainingBudget || 0), 0) : 0;
  const summarySubtitle = shared
    ? `Shared subaccount: ${formatNumber(shared.accountQuoteBalance ?? 0)} ${shared.quoteAsset} available · ${formatNumber(
        sharedPending
      )} ${shared.quoteAsset} pending`
    : "Remaining budget and subaccount balance per pair";

  const pairTitle = (st: PendingPairSummary) => `${st.baseAsset} / ${st.quoteAsset}`;

  return (
    <Stack spacing={{ xs: 3, md: 4 }}>
      {/* --- Pending summary per asset --- */}
      {summaryEntries.length > 0 && (
        <Box>
          <SectionHeader
            variant="page"
            icon={PieChartOutlineRoundedIcon}
            title="Pending by asset"
            subtitle={summarySubtitle}
          />
          {isPhone ? (
            // Phones: one card, a row per pair (pair · tasks · pending), instead of a tall card per pair.
            <SurfaceCard padding="none">
              {summaryEntries.map(([key, st], i) => (
                <Box
                  key={key}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    minHeight: 56,
                    px: 2.5,
                    py: 1.25,
                    borderTop: i > 0 ? `1px solid ${tokens.borderSubtle}` : "none",
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                      <Typography noWrap sx={{ fontSize: "0.9375rem", fontWeight: 700, letterSpacing: "-0.01em", minWidth: 0 }}>
                        {pairTitle(st)}
                      </Typography>
                      <StatusPill tone="neutral" size="sm" dot={false} label={taskMix(st)} />
                    </Stack>
                    {!shared && (
                      <Typography variant="caption" color="text.secondary" component="div" noWrap sx={{ mt: 0.25 }}>
                        Balance <Figure value={balance(st.accountQuoteBalance)} unit={st.quoteAsset} />
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                    {hasBuys(st) && (
                      <Typography sx={{ fontSize: "0.9375rem", fontWeight: 650 }} noWrap>
                        <Figure value={amount(st.remainingBudget)} unit={st.quoteAsset} />
                      </Typography>
                    )}
                    {hasSells(st) && (
                      <Typography variant="caption" color="text.secondary" component="div" noWrap>
                        <Figure value={amount(st.remainingSellBase)} unit={st.baseAsset} /> to sell
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </SurfaceCard>
          ) : (
            <Box
              sx={{
                display: "grid",
                // As many cards per row as fit at 240px: all three share one row on an iPad.
                gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 240px), 1fr))",
                gap: { xs: 1.5, md: 2 },
              }}
            >
              {summaryEntries.map(([key, st]) => (
                <SurfaceCard key={key}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1.5 }}>
                    <Typography noWrap sx={{ fontSize: { xs: "1rem", md: "1.0625rem" }, fontWeight: 700, letterSpacing: "-0.01em", minWidth: 0 }}>
                      {pairTitle(st)}
                    </Typography>
                    <StatusPill tone="neutral" size="sm" dot={false} label={taskMix(st)} />
                  </Stack>
                  {/* Buys (quote budget) and sells (base amount) as separate figures, never one sum. */}
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1.5 }}>
                    {hasBuys(st) && <Stat label="Pending buys" value={amount(st.remainingBudget)} unit={st.quoteAsset} />}
                    {hasSells(st) && <Stat label="Pending sells" value={amount(st.remainingSellBase)} unit={st.baseAsset} />}
                    {!shared && <Stat label="Account balance" value={balance(st.accountQuoteBalance)} unit={st.quoteAsset} />}
                  </Box>
                </SurfaceCard>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* --- Pending buybacks --- */}
      <Box>
        <SectionHeader
          variant="page"
          icon={ScheduleRoundedIcon}
          title="In progress"
          subtitle={`${pending.length} active buyback${pending.length === 1 ? "" : "s"}`}
        />
        {pending.length === 0 ? (
          <EmptyState>There are no pending buybacks at the moment. All caught up!</EmptyState>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 320px), 1fr))",
              gap: { xs: 1.5, md: 2 },
            }}
          >
            {[...pending]
              .sort((a, b) => b.lastUpdatedAt - a.lastUpdatedAt)
              .map((item) => {
                const progress = item.targetAmount > 0 ? (item.totalQuoteSpent / item.targetAmount) * 100 : 0;
                const tone = statusTone(item.status);
                const barColor = tone === "error" ? theme.palette.error.main : theme.palette.secondary.main;

                return (
                  <SurfaceCard key={item.uuid} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5}>
                      <Box sx={{ minWidth: 0 }}>
                        <BotLabel botId={item.botId} size="md" />
                        {/* What the task does, with its chain in words: the same pair runs on Arbitrum and
                            Ethereum, and both logos are blue. */}
                        <Typography variant="caption" color="text.secondary" component="div" noWrap sx={{ mt: 0.25 }}>
                          {chainName(chainOf(item.botId))} · Buying {item.baseAsset} with {item.quoteAsset}
                        </Typography>
                      </Box>
                      <StatusPill
                        tone={tone}
                        size="sm"
                        label={titleCase(item.status)}
                        pulsing={item.status === "ACTIVE"}
                        hollowDot={item.status === "PENDING"}
                      />
                    </Stack>

                    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", rowGap: 1.5, columnGap: 2 }}>
                      <Stat label="Bought" value={amount(item.totalBaseBought)} unit={item.baseAsset} />
                      <Stat label="Total cost" value={amount(item.totalQuoteSpent)} unit={item.quoteAsset} />
                      <Stat label="Target" value={amount(item.targetAmount)} unit={item.quoteAsset} />
                      <Stat label="Average price" value={formatAvgPrice(item)} />
                    </Box>

                    {/* Total cost and Target are in the tiles above: the bar only says how far along it is. */}
                    <Stack direction="row" alignItems="center" spacing={1.25}>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(progress, 100)}
                        aria-label={`${Math.round(progress)}% of target`}
                        sx={{
                          flex: 1,
                          height: 6,
                          bgcolor: alpha(barColor, 0.14),
                          "& .MuiLinearProgress-bar": { bgcolor: barColor },
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ flexShrink: 0 }}>
                        <Box component="span" sx={{ color: "text.primary", fontWeight: 700 }}>
                          {Math.round(progress)}%
                        </Box>{" "}
                        of target
                      </Typography>
                    </Stack>

                    <Typography variant="caption" color="text.secondary" sx={{ mt: "auto" }}>
                      Updated {formatDate(item.lastUpdatedAt)}
                    </Typography>
                  </SurfaceCard>
                );
              })}
          </Box>
        )}
      </Box>

      {/* --- Completed buybacks --- */}
      <Box>
        <SectionHeader
          variant="page"
          icon={TaskAltRoundedIcon}
          title="Completed"
          subtitle={`${completed.length} buyback${completed.length === 1 ? "" : "s"}`}
        />
        {completed.length === 0 ? (
          <EmptyState>No completed buybacks yet.</EmptyState>
        ) : (
          <SurfaceCard padding="none" sx={{ overflow: "hidden" }}>
            {[...completed]
              .sort((a, b) => b.lastUpdatedAt - a.lastUpdatedAt)
              .map((item) => (
                <Box
                  key={item.uuid}
                  sx={{
                    display: "grid",
                    alignItems: "center",
                    gap: { xs: 0.5, md: 2 },
                    gridTemplateColumns: {
                      xs: "1fr auto",
                      md: "minmax(160px, 1.2fr) minmax(120px, 1fr) minmax(140px, 1fr) minmax(140px, 1fr) minmax(110px, 0.8fr)",
                    },
                    px: { xs: 2, md: 3 },
                    py: { xs: 1.5, md: 1.75 },
                    borderBottom: `1px solid ${tokens.borderSubtle}`,
                    "&:last-of-type": { borderBottom: 0 },
                    "&:hover": { bgcolor: "rgba(255,255,255,0.02)" },
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <BotLabel botId={item.botId} size="sm" />
                    <Typography variant="caption" color="text.secondary" sx={{ display: { md: "none" } }}>
                      {formatDate(item.lastUpdatedAt)}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{ fontSize: "0.8125rem", color: "text.secondary", display: { xs: "none", md: "block" } }}
                    noWrap
                  >
                    {item.baseAsset} / {item.quoteAsset}
                  </Typography>
                  <Box sx={{ textAlign: { xs: "right", md: "left" }, minWidth: 0 }}>
                    <Typography sx={{ fontSize: "0.875rem", fontWeight: 650 }} noWrap>
                      {formatNumber(item.totalBaseBought, 4)}{" "}
                      <Box component="span" sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                        {item.baseAsset}
                      </Box>
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: { md: "none" } }}>
                      {formatNumber(item.totalQuoteSpent, 2)} {item.quoteAsset} · avg {formatAvgPrice(item)}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: "0.8125rem", display: { xs: "none", md: "block" } }} noWrap>
                    {formatNumber(item.totalQuoteSpent, 2)}{" "}
                    <Box component="span" sx={{ color: "text.secondary" }}>{item.quoteAsset}</Box>
                    <Box component="span" sx={{ color: "text.secondary", ml: 1 }}>
                      · avg {formatAvgPrice(item)}
                    </Box>
                  </Typography>
                  <Typography
                    sx={{ fontSize: "0.75rem", color: "text.secondary", textAlign: "right", display: { xs: "none", md: "block" } }}
                    noWrap
                  >
                    {formatDate(item.lastUpdatedAt)}
                  </Typography>
                </Box>
              ))}
          </SurfaceCard>
        )}
      </Box>
    </Stack>
  );
};

export default BuybacksView;
